import { put, call, takeEvery, takeLeading, select } from "redux-saga/effects";
import _pick from "lodash/pick";

import { safe } from "providers/GeneralProvider/saga";
import {
  getHealthPlaceListRequest,
  getHealthPlaceListSuccess,
  getHealthPlaceListFailure,
  getHealthPlaceDetailRequest,
  getHealthPlaceDetailSuccess,
  getHealthPlaceDetailFailure,
  createHealthPlaceRequest,
  createHealthPlaceSuccess,
  createHealthPlaceFailure,
  updateHealthPlaceRequest,
  updateHealthPlaceSuccess,
  updateHealthPlaceFailure,
  deleteHealthPlaceRequest,
  deleteHealthPlaceSuccess,
  deleteHealthPlaceFailure,
  restoreHealthPlaceRequest,
  restoreHealthPlaceSuccess,
  restoreHealthPlaceFailure,
} from "./slice";
import Helper from "utils/helpers";
import request from "utils/api";

function* getHealthPlaceList({ meta, payload }) {
  try {
    const params = _pick(payload, [
      "page",
      "limit",
      "search",
      "healthType",
      "latitude",
      "longitude",
      "radius",
      "minRating",
      "emergencyService",
      "prefecture",
      "district",
    ]);

    // Set defaults
    params.page = params.page || 1;
    params.limit = params.limit || 10;

    // Clean up empty params
    Object.keys(params).forEach((key) => {
      if (params[key] === "" || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });

    const { data } = yield call(request, "api/health/place", params, {
      method: "GET",
    });

    yield put(getHealthPlaceListSuccess(data, meta));
  } catch (error) {
    yield put(getHealthPlaceListFailure(error.message, meta));
    Helper.toastr("Lỗi", "Không thể tải danh sách cơ sở y tế", "error");
  }
}

function* getHealthPlaceDetail({ meta, payload }) {
  try {
    const params = {};

    // Add user location if available for distance calculation
    if (payload.latitude && payload.longitude) {
      params.latitude = payload.latitude;
      params.longitude = payload.longitude;
    }

    const { data } = yield call(
      request,
      `api/health/place/${payload.id}`,
      params,
      { method: "GET" }
    );

    yield put(getHealthPlaceDetailSuccess(data, meta));
  } catch (error) {
    yield put(getHealthPlaceDetailFailure(error.message, meta));
    Helper.toastr("Lỗi", "Không thể tải thông tin cơ sở y tế", "error");
  }
}

function* createHealthPlace({ meta, payload }) {
  try {
    const params = _pick(payload.data, [
      "placeName",
      "healthType",
      "phone",
      "address",
      "latitude",
      "longitude",
      "emergencyService",
      "description",
      "images",
      "thumbnail",
      "workingTimeMethod",
      "workingTime",
    ]);

    // Handle workingTimeMethod and workingHour based on API requirements
    if (params.workingTimeMethod === "custom") {
      // For custom hours, workingHour is REQUIRED
      if (params.workingTime && Array.isArray(params.workingTime)) {
        // Clean up workingHour data - remove any _id fields from all days
        params.workingHour = params.workingTime.map(day => {
          if (!day) {
            // Return default structure if day is null/undefined
            return {
              time: [],
              alwaysClose: false,
              alwaysOpen: false
            };
          }
          const cleanDay = { ...day };
          // Remove _id from day object if exists
          delete cleanDay._id;
          // Clean time array if exists
          if (cleanDay.time && Array.isArray(cleanDay.time)) {
            cleanDay.time = cleanDay.time.map(slot => {
              const { _id, ...cleanSlot } = slot;
              return cleanSlot;
            });
          }
          // Ensure required fields exist
          cleanDay.alwaysClose = cleanDay.alwaysClose || false;
          cleanDay.alwaysOpen = cleanDay.alwaysOpen || false;
          cleanDay.time = cleanDay.time || [];
          return cleanDay;
        });
      } else {
        // If custom but no workingTime provided, create default 7 days
        params.workingHour = Array(7).fill(null).map(() => ({
          time: [],
          alwaysClose: false,
          alwaysOpen: false
        }));
      }
    } else if (params.workingTimeMethod === "always_open") {
      // For always_open, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "always_open";
    } else if (params.workingTimeMethod === "always_close") {
      // For always_close, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "always_close";
    } else if (params.workingTimeMethod === "no_time") {
      // For no_time, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "no_time";
    } else {
      // Default to no_time if workingTimeMethod not specified
      params.workingTimeMethod = "no_time";
      delete params.workingHour;
    }

    // Clean up workingTime field - it's only for frontend
    delete params.workingTime;

    // Validate required fields
    if (!params.placeName || !params.healthType || !params.address) {
      throw new Error("Vui lòng điền đầy đủ thông tin bắt buộc");
    }

    // Validate coordinates
    if (params.latitude && params.longitude) {
      const lat = parseFloat(params.latitude);
      const lng = parseFloat(params.longitude);

      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        throw new Error("Tọa độ không hợp lệ");
      }
    }

    const { data } = yield call(request, "api/health/place", params, {
      method: "POST",
    });

    yield put(createHealthPlaceSuccess(data, meta));
    Helper.toastr("Thành công", "Đã tạo cơ sở y tế mới", "success");
  } catch (error) {
    yield put(createHealthPlaceFailure(error.message, meta));
    Helper.toastr("Lỗi", error.message || "Không thể tạo cơ sở y tế", "error");
    throw error;
  }
}

function* updateHealthPlace({ meta, payload }) {
  try {
    const { id, data } = payload;

    const params = _pick(data, [
      "placeName",
      "healthType",
      "phone",
      "address",
      "latitude",
      "longitude",
      "emergencyService",
      "description",
      "images",
      "thumbnail",
      "workingTimeMethod",
      "workingTime",
    ]);

    // Handle workingTimeMethod and workingHour
    if (params.workingTimeMethod === "custom") {
      // For custom hours, workingHour is REQUIRED
      if (params.workingTime && Array.isArray(params.workingTime)) {
        // Clean up workingHour data - remove any _id fields from all days
        params.workingHour = params.workingTime.map(day => {
          if (!day) {
            // Return default structure if day is null/undefined
            return {
              time: [],
              alwaysClose: false,
              alwaysOpen: false
            };
          }
          const cleanDay = { ...day };
          // Remove _id from day object if exists
          delete cleanDay._id;
          // Clean time array if exists
          if (cleanDay.time && Array.isArray(cleanDay.time)) {
            cleanDay.time = cleanDay.time.map(slot => {
              const { _id, ...cleanSlot } = slot;
              return cleanSlot;
            });
          }
          // Ensure required fields exist
          cleanDay.alwaysClose = cleanDay.alwaysClose || false;
          cleanDay.alwaysOpen = cleanDay.alwaysOpen || false;
          cleanDay.time = cleanDay.time || [];
          return cleanDay;
        });
      } else {
        // If custom but no workingTime provided, create default 7 days
        params.workingHour = Array(7).fill(null).map(() => ({
          time: [],
          alwaysClose: false,
          alwaysOpen: false
        }));
      }
    } else if (params.workingTimeMethod === "always_open") {
      // For always_open, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "always_open";
    } else if (params.workingTimeMethod === "always_close") {
      // For always_close, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "always_close";
    } else if (params.workingTimeMethod === "no_time") {
      // For no_time, do NOT send workingHour
      delete params.workingHour;
      params.workingTimeMethod = "no_time";
    } else {
      // Default to no_time if workingTimeMethod not specified
      params.workingTimeMethod = "no_time";
      delete params.workingHour;
    }

    // Clean up workingTime field - it's only for frontend
    delete params.workingTime;

    const { data: responseData } = yield call(
      request,
      `api/health/place/${id}`,
      params,
      { method: "PUT" }
    );

    yield put(updateHealthPlaceSuccess(responseData, meta));
    Helper.toastr("Thành công", "Đã cập nhật cơ sở y tế", "success");

    return responseData;
  } catch (error) {
    yield put(updateHealthPlaceFailure(error.message, meta));
    Helper.toastr("Lỗi", error.message || "Không thể cập nhật cơ sở y tế", "error");
    throw error;
  }
}

function* deleteHealthPlace({ meta, payload }) {
  try {
    const { id, _id } = payload;
    const placeId = id || _id;

    yield call(
      request,
      `api/health/place/${placeId}`,
      {},
      { method: "DELETE" }
    );

    yield put(deleteHealthPlaceSuccess({}, meta));
    Helper.toastr("Thành công", "Đã xóa cơ sở y tế", "success");

    // Refresh list after deletion
    const filters = yield select((state) => state.healthPlace.filters);
    yield put(getHealthPlaceListRequest(filters));
  } catch (error) {
    yield put(deleteHealthPlaceFailure(error.message, meta));
    Helper.toastr("Lỗi", "Không thể xóa cơ sở y tế", "error");
  }
}

function* restoreHealthPlace({ meta, payload }) {
  try {
    const { id, _id } = payload;
    const placeId = id || _id;

    const { data } = yield call(
      request,
      `api/health/place/${placeId}/restore`,
      {},
      { method: "PUT" }
    );

    yield put(restoreHealthPlaceSuccess(data, meta));
    Helper.toastr("Thành công", "Đã khôi phục cơ sở y tế", "success");

    // Refresh list after restoration
    const filters = yield select((state) => state.healthPlace.filters);
    yield put(getHealthPlaceListRequest(filters));
  } catch (error) {
    yield put(restoreHealthPlaceFailure(error.message, meta));
    Helper.toastr("Lỗi", "Không thể khôi phục cơ sở y tế", "error");
  }
}

function* healthPlaceSaga() {
  yield takeLeading(getHealthPlaceListRequest.type, safe(getHealthPlaceList));
  yield takeEvery(getHealthPlaceDetailRequest.type, safe(getHealthPlaceDetail));
  yield takeEvery(createHealthPlaceRequest.type, safe(createHealthPlace));
  yield takeEvery(updateHealthPlaceRequest.type, safe(updateHealthPlace));
  yield takeEvery(deleteHealthPlaceRequest.type, safe(deleteHealthPlace));
  yield takeEvery(restoreHealthPlaceRequest.type, safe(restoreHealthPlace));
}

export default healthPlaceSaga;
